import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import {
  Calendar,
  CalendarIcon,
  ChevronDown,
  X,
  AlertCircle,
  Copy,
  Loader2,
} from 'lucide-react';
import { Select } from '../../components/ui/Select';
import { useNdr } from '../../hooks/useNdr';
import { useAuth } from '../../hooks/useAuth';
import { formatDateTime } from '../../utils/format';
import { useModal } from '../../hooks/useModal';
import NdrDownloadModal from '../../components/NdrDownloadModal';
import DateRangeCalendar from '../../components/Calendar';
import { formatDate } from '../../libs/utils';
import { ordersService } from '../../services/orders';
import { useCouriers } from '../../hooks/useCourier';
import { useDebounce } from '../../hooks/useDebounce';
import NdrActionModal from '../../components/NdrActionModal';
import { NdrHistory } from '../../components/NdrHistory';
import toast from 'react-hot-toast';
import { useDrawer } from '../../hooks/useDrawer';
import TrackingDetails from '../../components/TrackingDetails';
import OrderDetails from '../../components/OrderDetails';

type TabType = 'Open' | 'Action Taken' | 'Closed';
const tabs: TabType[] = ['Open', 'Action Taken', 'Closed'];

export const pageSizeOptions = [
  { label: '10 / page', value: '10' },
  { label: '20 / page', value: '20' },
  { label: '50 / page', value: '50' },
  { label: '100 / page', value: '100' },
];

// Map tab names to API status codes
const TAB_STATUS_MAP: Record<TabType, number | undefined> = {
  Open: 1,
  'Action Taken': 3,
  Closed: 2,
};

export default function NDRPage() {
  const { user } = useAuth();
  const userId = user?.id as any;
  const { getNdrList, updateNdr, loading } = useNdr();

  const [activeTab, setActiveTab] = useState<TabType>('Open');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [selectedCourier, setSelectedCourier] = useState<string>('');
  const [allNdrData, setAllNdrData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { data: courierData } = useCouriers();
  const [showCalendar, setShowCalendar] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null);
  const { open: openDrawer, Drawer } = useDrawer();

  const { open, close, Modal } = useModal();
  const calendarRef = useRef<HTMLDivElement>(null);
  const admin = user?.roleName === 'ADMIN';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const RtoConfirmModal = ({ ndr }: { ndr: any }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onConfirm = async () => {
      setIsSubmitting(true);
      const loadingToast = toast.success('Updating RTO status...');
      try {
        await updateNdr(userId, {
          tracking_number: ndr.tracking_number,
          courier_id: ndr.courier_id,
          courier_remark: ndr.courier_remark,
          action: 'RETURN_SHIPMENT',
          delivery_attempt_count: ndr.delivery_attempt_count,
          is_fake: 0,
          additional_comment: 'RTO confirmed by user',
        });
        toast.success('Shipment marked for RTO', { id: loadingToast });
        fetchNdr();
        close();
      } catch (err: any) {
        toast.error(err?.message || 'Failed to update RTO', {
          id: loadingToast,
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="p-8 text-center space-y-6 max-w-sm mx-auto">
        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-2 animate-in zoom-in duration-300">
          <AlertCircle size={40} />
        </div>
        <div className="space-y-3">
          <h1 className="text-xl font-bold text-gray-800">NDR Action</h1>
          <h3 className="text-2xl font-bold text-gray-900">Confirm RTO</h3>
          <p className="text-gray-500 leading-relaxed">
            Are you sure you want to mark shipment{' '}
            <span className="font-bold text-gray-800">
              {ndr.tracking_number}
            </span>{' '}
            for Return to Origin?
          </p>
        </div>
        <div className="flex flex-col gap-3 pt-4">
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isSubmitting}
            className="w-full bg-red-500 hover:bg-red-600 text-white border-0 py-6 text-lg font-bold shadow-lg shadow-red-200 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            {isSubmitting ? 'Processing...' : 'Yes, Mark as RTO'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              close();
            }}
            disabled={isSubmitting}
            className="w-full text-gray-500 font-medium hover:bg-gray-50"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const isMountedRef = useRef(true);
  const isFirstMount = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch logic
  const fetchNdr = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await getNdrList({
        userId,
        page,
        pageSize: rowsPerPage,
        status: TAB_STATUS_MAP[activeTab], // Pass the status code dynamically
        search: debouncedSearch.trim() ? debouncedSearch.trim() : undefined,
        courier_id: selectedCourier ? selectedCourier : undefined,
        fromDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
        toDate: endDate ? endDate.toISOString().split('T')[0] : undefined,
      });

      if (isMountedRef.current) {
        setAllNdrData(res?.ndr_details || []);
        setTotal(res?.total_count || 0);
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        console.error('Failed to fetch NDR data:', err);
        setAllNdrData([]);
        setTotal(0);
      }
    }
  }, [userId, page, rowsPerPage, activeTab, debouncedSearch, selectedCourier, startDate, endDate, getNdrList]);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch]);

  // Refetch when page or tab changes
  useEffect(() => {
    fetchNdr();
  }, [fetchNdr]);

  // Reset page when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedRow(null);
  };

  const handleRto = (ndr: any) => {
    open({
      Component: () => <RtoConfirmModal ndr={ndr} />,
      closable: true,
      className:
        'w-full max-w-md rounded-3xl p-0 overflow-hidden shadow-2xl border-0',
    });
  };

  const handleTrackingClick = (ndr: any) => {
    openDrawer({
      element: (
        <TrackingDetails
          trackingId={ndr.tracking_number || ''}
          rtoTrackingId={ndr.rto_awb_number || null}
          orderStatus={ndr.status_text || ''}
        />
      ),
      position: 'right',
      className: 'p-4 sm:p-6 md:p-10 w-full max-w-4xl',
      closable: true,
    });
  };

  const handleOrderClick = async (ndr: any) => {
    const trackingNo = ndr.tracking_number;
    const orderNo = ndr.order_number;
    const identifier = trackingNo || orderNo;
    setLoadingOrderId(identifier);
    try {
      if (trackingNo) {
        const res = await ordersService.getOrders(userId, {
          user_id: String(userId),
          store_id: String(user?.storeId || '1'),
          shipment_status: 'all',
          page_size: 1,
          page: 1,
          only_count: false,
          search: [{ searchOn: 'awb_number', searchTerm: trackingNo }],
        });

        if (res?.results && res.results.length > 0) {
          setSelectedOrder(res.results[0]);
          return;
        }
      }

      if (orderNo) {
        const res2 = await ordersService.getOrders(userId, {
          user_id: String(userId),
          store_id: String(user?.storeId || '1'),
          shipment_status: 'all',
          page_size: 1,
          page: 1,
          only_count: false,
          search: [{ searchOn: 'order_number', searchTerm: orderNo }],
        });

        if (res2?.results && res2.results.length > 0) {
          setSelectedOrder(res2.results[0]);
          return;
        }
      }

      setSelectedOrder(ndr);
    } catch (err) {
      console.error('Failed to fetch full order details:', err);
      setSelectedOrder(ndr);
    } finally {
      setLoadingOrderId(null);
    }
  };

  const availableCouriersList = useMemo(() => {
    if (!courierData?.courier_list) return [];
    return courierData.courier_list
      .map((c) => ({ id: String(c.courier_id), name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [courierData]);

  const filteredNdrList = allNdrData;

  const totalPages = Math.ceil(total / rowsPerPage);

  if (selectedOrder) {
    // Map full order details or fallback NDR fields to OrderDetails structure
    const mappedOrder = {
      ...selectedOrder,

      // IDs
      order_id: selectedOrder.order_id ?? selectedOrder.id ?? selectedOrder.order_number,
      order_number: selectedOrder.order_number,
      shipment_id: selectedOrder.shipment_id,
      shopify_order_id: selectedOrder.shopify_order_id ?? null,

      // Dates
      created_at: selectedOrder.created_at,
      shipment_status_date: selectedOrder.shipment_status_date ?? selectedOrder.ndr_reported_at ?? null,

      // Status & Courier
      shipment_status: selectedOrder.shipment_status ?? selectedOrder.status_text ?? 'NDR',
      courier_name: selectedOrder.courier_name ?? null,
      courier_id: selectedOrder.courier_id ?? null,
      service_name: selectedOrder.service_name ?? selectedOrder.service_display_name ?? null,
      service_id: selectedOrder.service_id ?? null,
      awb_number: selectedOrder.awb_number ?? selectedOrder.tracking_number ?? null,
      label_url: selectedOrder.label_url ?? null,
      pickup_id: selectedOrder.pickup_id ?? null,
      shipment_message_detail:
        selectedOrder.shipment_message_detail ?? selectedOrder.courier_remark ?? null,
      shipment_type: selectedOrder.shipment_type ?? null,

      // Financials
      shipment_value: selectedOrder.shipment_value ?? selectedOrder.invoice_value ?? null,
      payment_mode: selectedOrder.payment_mode ?? null,
      cod_amount: selectedOrder.cod_amount ?? null,
      shipping_cost: selectedOrder.shipping_cost ?? null,

      // Package
      dead_weight: selectedOrder.dead_weight ?? selectedOrder.package_details?.package_dimension?.weight ?? null,
      weight_unit: selectedOrder.weight_unit ?? 'Kg',
      volumetric_weight: selectedOrder.volumetric_weight ?? selectedOrder.package_details?.volumetric_weight ?? null,
      chargeable_weight: selectedOrder.chargeable_weight ?? null,
      base_weight_slab: selectedOrder.base_weight_slab ?? null,
      length: selectedOrder.length ?? selectedOrder.package_details?.package_dimension?.length ?? null,
      breadth: selectedOrder.breadth ?? selectedOrder.package_details?.package_dimension?.width ?? null,
      height: selectedOrder.height ?? selectedOrder.package_details?.package_dimension?.height ?? null,
      dimension_unit: selectedOrder.dimension_unit ?? 'cm',
      package_desc: selectedOrder.package_desc ?? selectedOrder.package_details?.package_content ?? null,
      quantity: selectedOrder.quantity ?? selectedOrder.package_details?.no_of_items ?? null,

      // Routing
      zone: selectedOrder.zone ?? null,
      tat: selectedOrder.tat ?? null,
      shipping_mode: selectedOrder.shipping_mode ?? null,
      channel_id: selectedOrder.channel_id ?? null,

      // Addresses
      origin_address: selectedOrder.origin_address ?? null,
      destination_address:
        selectedOrder.destination_address ?? selectedOrder.delivery_address ?? null,
      return_address: selectedOrder.return_address ?? null,

      // Store & Tags
      store_id: selectedOrder.store_id ?? null,
      shop_name: selectedOrder.shop_name ?? null,
      customer_note: selectedOrder.customer_note ?? null,
      tags: selectedOrder.tags ?? null,

      // Details
      rate_details: selectedOrder.rate_details ?? null,
      package_detail: selectedOrder.package_detail ?? selectedOrder.package_details ?? null,
      products: selectedOrder.products ?? null,
    };
    return (
      <OrderDetails order={mappedOrder} onBack={() => setSelectedOrder(null)} />
    );
  }

  return (
    <div className="px-4 xl:px-0 py-4 xl:py-6">
      <div className="space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-text">NDR</h1>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Review undelivered shipments and take necessary action.</span>
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-4 mb-0">
          <div className="w-full xl:w-80">
            <Input
              placeholder={
                admin
                  ? 'Search by Tracking Number or CRN'
                  : 'Search by Tracking Number'
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="w-full xl:w-48">
            <Select
              value={selectedCourier}
              onChange={(e) => {
                setSelectedCourier(e.target.value);
                setPage(1);
              }}
              placeholder="Select Courier"
              options={[
                { label: 'All Couriers', value: '' },
                ...availableCouriersList.map((c) => ({ label: c.name, value: c.id }))
              ]}
            />
          </div>

          <div className="relative" ref={calendarRef}>
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="flex items-center gap-2 border px-3 py-2 rounded-lg bg-white hover:bg-gray-50 text-teal"
            >
              <CalendarIcon size={16} />
              {startDate && endDate
                ? `${formatDate(startDate.toISOString())} - ${formatDate(endDate.toISOString())}`
                : 'Select Date'}
            </button>

            {showCalendar && (
              <div className="absolute z-50 mt-2">
                <DateRangeCalendar
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(start: Date | null, end: Date | null) => {
                    setStartDate(start);
                    setEndDate(end);
                  }}
                />
              </div>
            )}
          </div>

          {(search || selectedCourier || startDate || endDate) && (
            <Button
              variant="outline"
              onClick={() => {
                setSearch('');
                setSelectedCourier('');
                setStartDate(null);
                setEndDate(null);
                setPage(1);
                toast.success('Filters cleared');
              }}
              className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              Reset Filters
            </Button>
          )}

          {admin && (
            <button
              onClick={() =>
                open({
                  Component: () => (
                    <NdrDownloadModal
                      couriers={availableCouriersList}
                      fromDate={
                        startDate ? startDate.toISOString().split('T')[0] : ''
                      }
                    />
                  ),
                  closable: true,
                })
              }
              className="bg-teal text-white p-2 rounded-lg ml-auto "
            >
              Download Report
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between border-b mb-6">
          <div className="flex gap-12 text-lg font-semibold overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`pb-2 whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-b-2 border-teal text-text font-medium'
                    : 'text-gray-800'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto w-full pb-4">
          <div className="space-y-3 min-w-[900px] lg:min-w-full">
            {/* Detached Table Header Card */}
            <div className="bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
              <div
                className={`grid gap-2 xl:gap-4 text-center items-center ${admin ? '[grid-template-columns:80px_1fr_1fr_100px_repeat(5,1fr)]' : '[grid-template-columns:1fr_1fr_100px_repeat(5,1fr)]'}`}
              >
                {admin && <span>CRN</span>}
                <div>Order Info</div>
                <div>Customer Info</div>
                <div>Payment</div>
                <div>Tracking</div>
                <div>NDR Date</div>
                <div>Attempts</div>
                <div>Remark</div>
                <div>Action</div>
              </div>
            </div>

            {loading ? (
              <div className="py-20 text-center animate-pulse  rounded-xl border">
                Loading NDR data...
              </div>
            ) : filteredNdrList.length === 0 ? (
              <div className="py-20 text-center text-text3  rounded-xl border">
                No records found for {activeTab}
              </div>
            ) : (
              filteredNdrList.map((ndr, i) => (
                <div
                  key={i}
                  className="bg-card border rounded-xl px-3 xl:px-5 py-2 shadow-sm text-center transition-all duration-200 hover:shadow-md"
                >
                  <div
                    className={`grid gap-2 xl:gap-4 text-xs xl:text-sm text-gray-700 items-center ${
                      admin
                        ? '[grid-template-columns:80px_1fr_1fr_100px_repeat(5,1fr)]'
                        : '[grid-template-columns:1fr_1fr_100px_repeat(5,1fr)]'
                    }`}
                  >
                    {/* CRN */}
                    {admin && (
                      <div className="text-center font-medium text-text2">
                        {ndr.crn_number || ndr.crn || 'N/A'}
                      </div>
                    )}

                    {/* Order Info */}
                    <div
                      onClick={() => handleOrderClick(ndr)}
                      className="text-blue-600 font-semibold cursor-pointer hover:underline flex items-center justify-center gap-1.5"
                    >
                      {loadingOrderId === (ndr.tracking_number || ndr.order_number) ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                          <span>Loading...</span>
                        </>
                      ) : (
                        ndr.order_number
                      )}
                    </div>

                    {/* Customer Info */}
                    <div className="space-y-1">
                      <div className="font-semibold text-text">
                        {ndr.delivery_address?.contact_name}
                      </div>
                      <div className="text-text2 text-sm">
                        {ndr.delivery_address?.contact_no}
                      </div>
                    </div>

                    {/* Payment */}
                    <div className="font-bold text-teal">
                      ₹{(ndr.invoice_value ?? 0).toLocaleString('en-IN')}
                    </div>

                    {/* Tracking */}
                    <div className="space-y-1 group">
                      <div className=" font-semibold flex items-center justify-center gap-1">
                        <span
                          className="cursor-pointer hover:underline"
                          onClick={() => handleTrackingClick(ndr)}
                        >
                          {ndr.courier_name}
                        </span>
                      </div>
                      <div className="text-sm text-text2 flex items-center justify-center gap-2">
                        <span
                          className="cursor-pointer hover:underline"
                          onClick={() => handleTrackingClick(ndr)}
                        >
                          {ndr.tracking_number}
                        </span>
                        <button
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-gray-50 rounded hover:bg-gray-100"
                          onClick={() => {
                            navigator.clipboard.writeText(ndr.tracking_number);
                            toast.success('Tracking number copied');
                          }}
                        >
                          <Copy size={12} className="text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* NDR Date */}
                    <div className="text-sm text-text2">
                      {formatDateTime(ndr.ndr_reported_at || ndr.delivery_deadline_date).date} <br />
                      {formatDateTime(ndr.ndr_reported_at || ndr.delivery_deadline_date).time}
                    </div>

                    {/* Attempts */}
                    <div className="flex justify-center">
                      <span className="bg-orange-50 text-orange-600 px-3 py-1 rounded-full text-xs font-semibold border border-orange-100">
                        {ndr.delivery_attempt_count} Attempts
                      </span>
                    </div>

                    {/* Remark */}
                    <div className="text-sm text-gray-500 italic line-clamp-3 px-2 leading-relaxed">
                      "{ndr.courier_remark || 'No remark available'}"
                    </div>

                    {/* Action */}
                    <div className="flex flex-col gap-2 items-center">
                      {activeTab === 'Open' ? (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              open({
                                Component: () => (
                                  <NdrActionModal
                                    ndr={ndr}
                                    onClose={close}
                                    onSuccess={fetchNdr}
                                    initialAction="REATTEMPT"
                                  />
                                ),
                                closable: true,
                                className:
                                  'w-full max-w-2xl max-h-[88%] rounded-2xl p-0 overflow-y-scroll overflow-x-hidden shadow-2xl border-0',
                              });
                            }}
                            className="w-24 py-1.5 bg-teal/10 text-teal border border-teal/20 rounded-md text-sm font-bold hover:bg-teal hover:text-white transition-all shadow-sm hover:shadow-teal/20"
                          >
                            Re-Attempt
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleRto(ndr);
                            }}
                            className="w-24 py-1.5 bg-red-50 text-red-500 border border-red-100 rounded-md text-sm font-bold hover:bg-red-500 hover:text-white transition-all shadow-sm hover:shadow-red-700/10"
                          >
                            RTO
                          </button>
                        </>
                      ) : activeTab === 'Action Taken' ? (
                        <button
                          onClick={() =>
                            setExpandedRow(
                              expandedRow === ndr.tracking_number
                                ? null
                                : ndr.tracking_number
                            )
                          }
                          className="px-4 py-1.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-md text-xs font-bold hover:bg-blue-600 hover:text-white transition-all shadow-sm  tracking-wider"
                        >
                          {expandedRow === ndr.tracking_number
                            ? 'Hide'
                            : 'View'}{' '}
                          History
                        </button>
                      ) : (
                        <span className="text-gray-400 font-medium italic text-xs">
                          No Action Needed
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded History Section */}
                  {expandedRow === ndr.tracking_number && (
                    <div className="mt-4 pt-4 border-t border-dashed border-gray-100 animate-in slide-in-from-top-2 duration-300">
                      <NdrHistory trackingNo={ndr.tracking_number} />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination */}
        {!loading && filteredNdrList.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing page {page} of {totalPages || 1}
            </div>
            <div className="flex items-center gap-3">
              <Select
                options={pageSizeOptions}
                value={String(rowsPerPage)}
                position="up"
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(1);
                }}
              />
              <Button
                variant="outline"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, Math.max(totalPages, 1)))
                }
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
      <Drawer />
      <Modal />
    </div>
  );
}
